import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { fromJS, List } from 'immutable';
import moment from 'moment';

import { loadCountryPhoneCodes } from 'src/actions/company/companyTarget';
import { fetchEventActivities } from 'src/actions/eventActivities';
import { sendOutgoingCall } from 'src/actions/dialPadCall';

import InputErrorDisplay from '../decorators/InputErrorDisplay';
import connectOptions, { mergeProps } from '../../utils/connectOptions';
import AutoCompleteContainer from '../AutoComplete';
import { unwrap, getChanged, getIn } from '../../utils/ChangeSpy';
import Popups from '../../components/Popup';
import {
  resetCurrentEvent,
  startEditCompanyEvent,
  changeCompanyEvent,
  saveCompanyEvent,
  findDirectors,
  findUsers,
  findAnalysts,
  closeValidationError,
  reloadEvents,
  findDealMakers,
  checkNextActionExisting,
} from '../../actions/company/companyEvents';
import isVisible from '../../utils/isVisible';
import { publish } from '../../decorators/tabChange';
import { showError, showInformation } from '../../utils/MessagePopup';
import { usePopupContext } from '../../contexts/PopupContext';

const DATE_FORMAT = 'YYYY-MM-DD';

const MAX_VALUE = 1000;

const SUCCESSFUL_ACTIVITY_REPLICATION_MESSAGE = `<h4>Completed activity has been successfully replicated to the company(ies):</h4>`;
const FAILURE_ACTIVITY_REPLICATION_MESSAGE = `<h4>The following company(ies) cannot be used for the activity replication:</h4>`;

const DIALPAD_URL = 'https://dialpad.com/app';

const isValidDate = date => {
  if (!date) return false;

  return !!date.isValid && date.isValid();
};

const isValid = (event, hasEverNext) => {
  if (!isValidDate(unwrap(event.get('date')))) return false;
  if (unwrap(event.get('transactionValueAtClose')) > MAX_VALUE) return false;
  if (unwrap(event.get('ttmEbitdaAtClose')) > MAX_VALUE) return false;
  if (hasEverNext && !unwrap(event.get('completed'))) return false;

  return !!unwrap(event.getIn(['activityMap', 'selected', 'value']));
};

/**
 * Return company-based information message string.
 *
 * @param {string} name Company name.
 * @param {number} id Company id.
 */
const getCompanyEventMessage = ({ companyLegalName, companyId }) => {
  const { pathname, origin } = window.location;
  const endPath = pathname.split('/').pop() === 'target' ? 'target' : 'buyer';

  return `${companyLegalName} ( ${origin}/company/${companyId}/${endPath} )`;
};

/**
 * Configure messages section by company events.
 *
 * @param {Array} events Company events list.
 * @param {string} partitionHeader Messages partition header.
 */
const getCompanyEventMessages = (events, partitionHeader) => {
  const informationTexts = [];

  if (events.length > 0) {
    informationTexts.push(partitionHeader);

    const messages = events.map(event => getCompanyEventMessage(event));

    informationTexts.push(...messages);
  }

  return informationTexts;
};

/**
 * Parse companies events from the request payload and configure alert messages.
 *
 * @param {object} saveResponse Save company event response payload.
 */
const getReplicatedCompaniesMessages = saveResponse => {
  const messages = [];

  if (saveResponse && Object.prototype.hasOwnProperty.call(saveResponse, 'replicatedCompanies')) {
    const { replicatedCompanies } = saveResponse;

    if (Object.entries(replicatedCompanies).length > 0) {
      const { addedEvents, skipped } = replicatedCompanies;

      const addedEventsMessages = getCompanyEventMessages(addedEvents, SUCCESSFUL_ACTIVITY_REPLICATION_MESSAGE);
      const skippedMessages = getCompanyEventMessages(skipped, FAILURE_ACTIVITY_REPLICATION_MESSAGE);

      messages.push(...addedEventsMessages, ...skippedMessages);
    }
  }

  return messages;
};

/**
 * Let fill out default director, analyst and user data if users create a new event.
 */
const EventCompanyPopupContainer = props => {
  const {
    activities,
    backloggedActivities,
    buyer,
    changeCompanyEvent,
    checkNextActionExisting,
    companyBuyer,
    companyContacts,
    companyInfo,
    defaultAnalyst,
    defaultDealMaker,
    defaultDirector,
    entityId,
    entityType,
    events,
    fetchEventActivities,
    findAnalysts,
    findDealMakers,
    findDirectors,
    findUsers,
    notifyTab,
    phoneCodes,
    popup,
    reloadEvents,
    resetCurrentEvent,
    saveCompanyEvent,
    sendOutgoingCall,
    startEditCompanyEvent,
    user,
    children,
  } = props;

  const { openPopup, closePopup, onClosePopup } = usePopupContext();

  const [firstTime, setFirstTime] = useState(true);
  const [selectedCompaniesIds, setSelectedCompaniesIds] = useState([]);

  // Not tied to any prop/state comparison, matches the original componentDidUpdate's own
  // `this.needMoveFocusToNext` instance flag exactly - genuine cross-render mutable flag.
  const needMoveFocusToNextRef = useRef(false);
  const hasMountedRef = useRef(false);
  const previousRenderRef = useRef({ popup, companyInfo, events });
  const shortcutKeysRef = useRef([]);

  const shouldPrompt = false;

  const handleClose = useCallback(() => {
    closePopup();
  }, [closePopup]);

  // Read events from the store, not from props: this check runs from callbacks captured earlier
  // (the mount-time onClosePopup handler, reloadEvents afterSuccess) and must see the reloaded events.
  const isNaExisting = useCallback(() => checkNextActionExisting(), [checkNextActionExisting]);

  const getPhoneCode = useCallback(
    countryCode => {
      if (phoneCodes.size === 0) {
        return '';
      }

      const phoneCodeEntry = phoneCodes.find(entry => entry.get('code') === countryCode);

      if (phoneCodeEntry) {
        return phoneCodeEntry.get('dialCode');
      }
    },
    [phoneCodes],
  );

  const getCountryCodeByCountryName = useCallback(
    countryName => {
      if (phoneCodes.size === 0) {
        return '';
      }

      const phoneCodeEntry = phoneCodes.find(entry => entry.get('name') === countryName);

      if (phoneCodeEntry) {
        return phoneCodeEntry.get('code');
      }
    },
    [phoneCodes],
  );

  const moveFocus = useCallback(() => {
    const element = document.querySelector('textarea[name="description"]');

    // Check if element is visible in DOM, otherwise delay moving focus until DOM updated
    if (element && element.offsetParent) {
      element.focus();
    } else {
      needMoveFocusToNextRef.current = true;
    }
  }, []);

  const handleNext = useCallback(() => {
    moveFocus();
  }, [moveFocus]);

  const handleSuggestChange = useCallback(({ name, value }) => changeCompanyEvent({ field: name, value }), [
    changeCompanyEvent,
  ]);

  const suggestUserPrefix = 'current.';
  const suggestUser = (
    <AutoCompleteContainer
      idName={`${suggestUserPrefix}userId`}
      inputProps={{
        name: 'userName',
        className: 'form-control',
        label: 'Completed by',
        tabIndex: -1,
      }}
      name={`${suggestUserPrefix}userName`}
      onChange={handleSuggestChange}
      onFind={options => {
        findUsers({ ...options, field: `${suggestUserPrefix}userName` });
      }}
      rootPath={['targetCompany', 'events']}
      suggestionsName={`${suggestUserPrefix}userSuggests`}
      valueName={`${suggestUserPrefix}userName`}
    />
  );
  const suggestDirector = (
    <AutoCompleteContainer
      idName={`${suggestUserPrefix}recordOwnerId`}
      inputProps={{
        name: 'directorName',
        placeholder: 'Module',
        className: 'form-control',
        label: 'Team',
      }}
      name={`${suggestUserPrefix}directorName`}
      onChange={handleSuggestChange}
      onFind={options => {
        findDirectors({ ...options, field: `${suggestUserPrefix}directorName` });
      }}
      rootPath={['targetCompany', 'events']}
      suggestionsName={`${suggestUserPrefix}directorSuggests`}
      valueName={`${suggestUserPrefix}directorName`}
    />
  );
  const suggestAnalyst = (
    <AutoCompleteContainer
      idName={`${suggestUserPrefix}recordSubOwnerId`}
      inputProps={{
        name: 'analystName',
        label: 'Analyst',
        placeholder: 'Analyst',
        className: 'form-control',
      }}
      name={`${suggestUserPrefix}analystName`}
      onChange={handleSuggestChange}
      onFind={options => {
        findAnalysts({ ...options, field: `${suggestUserPrefix}analystName` });
      }}
      rootPath={['targetCompany', 'events']}
      suggestionsName={`${suggestUserPrefix}analystSuggests`}
      valueName={`${suggestUserPrefix}analystName`}
    />
  );
  const suggestDealMaker = (
    <AutoCompleteContainer
      idName={`${suggestUserPrefix}dealMakerId`}
      inputProps={{
        name: 'dealMakerName',
        label: 'Dealmaker',
        placeholder: 'Dealmaker',
        className: 'form-control',
      }}
      name={`${suggestUserPrefix}dealMakerName`}
      onChange={handleSuggestChange}
      onFind={options => {
        findDealMakers({ ...options, field: `${suggestUserPrefix}dealMakerName` });
      }}
      rootPath={['targetCompany', 'events']}
      suggestionsName={`${suggestUserPrefix}dealMakerSuggests`}
      valueName={`${suggestUserPrefix}dealMakerName`}
    />
  );

  const handleShowNextActionConfirmPopup = useCallback(() => {
    openPopup('ConfirmPopup', {
      message:
        'Warning: You are about to close this window without setting a Next Action. Are you sure you wish to proceed?',
      onOk: handleClose,
      onCancel: popup.getIn(['props', 'onReopen'], () => {}),
      yes: 'Yes',
      no: 'No',
    });
    setFirstTime(true);
  }, [openPopup, popup, handleClose]);

  const handleCloseEventPopup = useCallback(() => {
    if (!isNaExisting()) return handleShowNextActionConfirmPopup();

    // Timeout prevents call maximum stack size.
    setTimeout(() => {
      handleClose();
    }, 0);
  }, [isNaExisting, handleShowNextActionConfirmPopup, handleClose]);

  const handleCallAfterSuccess = useCallback(() => {
    window.open(DIALPAD_URL);
  }, []);

  const handleCallAfterError = useCallback(() => {
    showError(openPopup, ['Error. Call initiation failed']);
  }, [openPopup]);

  const handleCall = useCallback(
    (phoneNumber, phoneCode, eventId) => {
      openPopup('OutgoingCallPopup');
      sendOutgoingCall({
        phoneNumber,
        phoneCode,
        eventId,
        afterSuccess: handleCallAfterSuccess,
        afterError: handleCallAfterError,
      });
    },
    [openPopup, sendOutgoingCall, handleCallAfterSuccess, handleCallAfterError],
  );

  const handleChange = useCallback(
    event => {
      const { name, type, checked, value } = event.target;

      if (name === 'phone') {
        changeCompanyEvent({ field: 'phone', value });
      }

      if (name === 'country') {
        changeCompanyEvent({ field: 'country', value });
        changeCompanyEvent({ field: 'phoneCode', value: getPhoneCode(value) });
      }

      changeCompanyEvent({
        field: name,
        value: type && type.toUpperCase() === 'CHECKBOX' ? checked : value,
      });

      if (name === 'completed' && checked) {
        changeCompanyEvent({ field: 'date', value: moment() });
        changeCompanyEvent({ field: 'userName', value: user.get('userName') });
        changeCompanyEvent({ field: 'userId', value: user.get('id') });
      }
    },
    [changeCompanyEvent, getPhoneCode, user],
  );

  const handleActivityChange = useCallback(
    data => {
      changeCompanyEvent({ field: 'activityMap', value: data });
      changeCompanyEvent({ field: 'emailText.value', value: '' });
      if (data.selected && data.selected.value) {
        moveFocus();
      }
    },
    [changeCompanyEvent, moveFocus],
  );

  const handleChangeCompanyIds = useCallback(
    event => {
      const value = Number(event.target.value);

      if (selectedCompaniesIds.includes(value)) {
        setSelectedCompaniesIds(selectedCompaniesIds.filter(id => id !== value));
      } else {
        setSelectedCompaniesIds([...selectedCompaniesIds, value]);
      }
    },
    [selectedCompaniesIds],
  );

  const handleAddBuyerNote = useCallback(() => {
    const showEmail = !events.getIn(['current', 'activityMap', 'selected', 'showEmail']);

    changeCompanyEvent({ field: 'activityMap.selected.showEmail', value: showEmail });
  }, [events, changeCompanyEvent]);

  const reopenPopup = useCallback(
    approach => {
      if (isNaExisting()) return handleCloseEventPopup();

      popup.getIn(['props', 'onReopen'], () => {})(); // reopen popup

      const extra = {
        defaultUser: {
          id: user.get('id'),
          text: user.get('userName'),
        },
        defaultAnalyst,
        defaultDealMaker,
        defaultDirector,
      };

      startEditCompanyEvent({
        approach,
        companyInfo,
        companyBuyer,
        companyContacts,
        ...extra,
      });

      onClosePopup(handleCloseEventPopup);
    },
    [
      isNaExisting,
      handleCloseEventPopup,
      popup,
      user,
      defaultAnalyst,
      defaultDealMaker,
      defaultDirector,
      startEditCompanyEvent,
      companyInfo,
      companyBuyer,
      companyContacts,
      onClosePopup,
    ],
  );

  /**
   * Resolve callback after the save company request.
   *
   * @param {object} body Save company event request body.
   * @param {object} saveResponse Save company event response payload.
   */
  const promptEvent = useCallback(
    (body, saveResponse) => {
      const messages = getReplicatedCompaniesMessages(saveResponse);

      notifyTab({ id: body.id }, 'onEventChange');

      reloadEvents({
        entityId,
        afterSuccess: () => {
          handleClose();

          if (messages.length > 0) {
            showInformation(openPopup, messages.join('\n'), () => {
              reopenPopup(body.approach);
            });
          } else {
            reopenPopup(body.approach);
          }
        },
      });
    },
    [notifyTab, reloadEvents, entityId, handleClose, openPopup, reopenPopup],
  );

  const handleSave = useCallback(
    event => {
      event.preventDefault();

      const eventCurrent = events.get('current');
      const current = getChanged(eventCurrent);

      if (current.date) {
        current.date = current.date.format(DATE_FORMAT);
      }

      if (current.projectedCloseDate) {
        current.projectedCloseDate = current.projectedCloseDate.format(DATE_FORMAT);
      }

      if (current.projectedCloseDate === '') {
        current.projectedCloseDate = null;
      }

      if (current.contactId === undefined || current.contactId === 'undefined') {
        current.contactId = null;
      }

      if (current.transactionCloseDate) {
        current.transactionCloseDate = current.transactionCloseDate.format(DATE_FORMAT);
      }

      Object.keys(current).forEach(key => {
        if (/Name$/.test(key)) {
          delete current[key];
        }
      });
      delete current.activityMap;

      const body = {
        ...current,
        id: events.getIn(['current', 'id']),
        approach: events.getIn(['current', 'approach']),
        activity: events.getIn(['current', 'activityMap', 'selected', 'name']),
        meta: false,
        backlogged: getIn(events, ['current', 'backlogged'], false) || false,
        selectedCompaniesIds,
        phoneRegionCode: unwrap(events.getIn(['current', 'country'])),
      };

      if (body.contactId) {
        body[`${body.approach}ContactId`] = body.contactId;
      }

      const form = event.target;

      for (let index = 0; index < form.length; index++) {
        if (!isVisible(form[index]) && form[index].name !== 'description') delete body[form[index].name];
      }
      ['recordOwnerId', 'recordSubOwnerId', 'dealMakerId'].forEach(field => {
        body[field] = body[field] || undefined;
      });

      saveCompanyEvent({
        entityType,
        entityId,
        body,
        afterSuccess: ({ response }) => promptEvent(body, response),
        afterError: ({ responseBody }) => {
          const texts = Array.isArray(responseBody?.errors) ? responseBody.errors.map(error => error.toString()) : [];
          const defaultSubheader = responseBody
            ? 'Backlogging Failed due to missing data, and no targets were Backlogged. The following profiles were missing information:'
            : 'An error occurred';

          openPopup('InformationPopup', {
            message: {
              subheader: responseBody?.message || defaultSubheader,
              className: 'text-danger',
              texts,
            },
            header: (
              <span>
                <i aria-hidden="true" className="fa fa-exclamation-triangle text-danger" />
                Error Alert
              </span>
            ),
            onClosePopupCallBack: () => {
              reopenPopup();
            },
          });
        },
      });
    },
    [events, selectedCompaniesIds, saveCompanyEvent, entityType, entityId, promptEvent, openPopup, reopenPopup],
  );

  const saveCommandCapture = useCallback(
    event => {
      const currentEvent = events.get('current');
      const hasEverNext =
        events
          .get('all')
          .filter(otherEvent => otherEvent.get('id') !== currentEvent.get('id') && unwrap(otherEvent.get('isNext')))
          .size > 0;

      if (isValid(currentEvent, hasEverNext) && !currentEvent.get('isLoading')) {
        handleSave(event);
      }
    },
    [events, handleSave],
  );

  // Ctrl+Enter saves the event. Matches only when Enter is the first key pressed while Ctrl alone is held.
  useEffect(() => {
    const handleKeyDown = event => {
      if (!event.ctrlKey && !event.altKey && !event.shiftKey) return;

      if (!['Control', 'Alt', 'Shift'].includes(event.key)) {
        shortcutKeysRef.current.push(event.key);
      }

      const keys = shortcutKeysRef.current;

      if (event.ctrlKey && !event.altKey && !event.shiftKey && keys.length === 1 && keys[0] === 'Enter') {
        shortcutKeysRef.current = [];
        saveCommandCapture(event);
      }
    };

    const handleKeyUp = event => {
      if (!event.ctrlKey && !event.altKey && !event.shiftKey) {
        shortcutKeysRef.current = [];
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keyup', handleKeyUp, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keyup', handleKeyUp, true);
    };
  }, [saveCommandCapture]);

  useEffect(() => {
    const approach = popup.getIn(['props', 'approach']);
    const eventId = popup.getIn(['props', 'event', 'id']);

    const extra = {
      defaultUser: {
        id: user.get('id'),
        text: user.get('userName'),
      },
    };

    if (!eventId) {
      extra.defaultAnalyst = defaultAnalyst;
      extra.defaultDirector = defaultDirector;
      extra.defaultDealMaker = defaultDealMaker;
    }

    if (isNaExisting()) {
      extra.completed = true;
    }

    startEditCompanyEvent({
      eventId,
      approach,
      companyInfo,
      companyBuyer,
      companyContacts,
      ...extra,
    });
    fetchEventActivities();

    onClosePopup(handleCloseEventPopup);

    return () => {
      resetCurrentEvent();
    };
    // Mount-only: matches the original componentDidMount/componentWillUnmount pair exactly.
  }, []);

  // Mirrors the original componentDidUpdate(prevProps) exactly, including its use of the
  // PREVIOUS render's popup/companyInfo/events (not the current ones) inside the body while
  // gating on the CURRENT phoneCodes - preserved as-is, not "fixed", per this migration's rule
  // of not silently changing pre-existing (possibly unintentional) behavior.
  useEffect(() => {
    if (hasMountedRef.current) {
      const {
        popup: previousPopup,
        companyInfo: previousCompanyInfo,
        events: previousEvents,
      } = previousRenderRef.current;

      if (phoneCodes.size > 0) {
        const currentEvent = previousEvents.get('current');
        const eventId = previousPopup.getIn(['props', 'event', 'id']);

        const phoneRegionCode = previousPopup.getIn(['props', 'event', 'phoneRegionCode']) ?? 'US';
        const companyCountryName = unwrap(previousCompanyInfo.getIn(['country']));
        const companyCountryCode = getCountryCodeByCountryName(companyCountryName) ?? 'US';
        const countryCode = eventId ? phoneRegionCode : companyCountryCode;

        if (typeof unwrap(currentEvent.get('phone')) !== 'string' && eventId === undefined) {
          const companyPhone = unwrap(previousCompanyInfo.get('phone'));

          changeCompanyEvent({ field: 'phone', value: companyPhone });
        }

        if (firstTime) {
          const phoneCode = getPhoneCode(countryCode);

          changeCompanyEvent({ field: 'country', value: countryCode });
          changeCompanyEvent({ field: 'phoneCode', value: phoneCode });
        }

        setFirstTime(false);
      }

      if (needMoveFocusToNextRef.current) moveFocus();
      needMoveFocusToNextRef.current = false;
    } else {
      hasMountedRef.current = true;
    }

    previousRenderRef.current = { popup, companyInfo, events };
  });

  const currentEvent = events.get('current');

  const hasEverNext =
    events
      .get('all')
      .filter(otherEvent => otherEvent.get('id') !== currentEvent.get('id') && unwrap(otherEvent.get('isNext'))).size >
    0;

  const contacts = companyContacts
    .map(contact =>
      fromJS({
        name: unwrap(contact.get('fullName')),
        value: contact.get('id'),
      }),
    )
    .insert(0, fromJS({ name: 'Select a Contact', value: '' }));
  const canSave = isValid(currentEvent, hasEverNext) && !currentEvent.get('isLoading');

  return (
    <>
      <Popups.EventPopup
        activities={activities}
        backloggedActivities={backloggedActivities}
        buyer={buyer}
        canSave={canSave}
        contacts={contacts}
        event={currentEvent}
        hasEverNext={hasEverNext}
        loading={currentEvent.get('isLoading')}
        onActivityChange={handleActivityChange}
        onAddBuyerNote={handleAddBuyerNote}
        onCall={handleCall}
        onChange={handleChange}
        onChangeCompanyIds={handleChangeCompanyIds}
        onClose={handleCloseEventPopup}
        onNext={handleNext}
        onSave={handleSave}
        shouldPrompt={shouldPrompt}
        suggestAnalyst={suggestAnalyst}
        suggestDealMaker={suggestDealMaker}
        suggestDirector={suggestDirector}
        suggestUser={suggestUser}
        user={user}
      />
      {children}
    </>
  );
};

function mapStateToProps(state, props) {
  return {
    ...props,
    defaultDirector: {
      id: state.targetCompany.target.getIn(['recordOwnerId', 'value']),
      text: state.targetCompany.target.getIn(['suggestDirector', 'value']),
    },
    defaultAnalyst: {
      id: state.targetCompany.target.getIn(['recordSubOwnerId', 'value']),
      text: state.targetCompany.target.getIn(['suggestAnalyst', 'value']),
    },
    defaultDealMaker: {
      id: state.targetCompany.target.getIn(['dealMakerId', 'value']),
      text: state.targetCompany.target.getIn(['suggestDealMaker', 'value']),
    },
    buyer: state.targetCompany.buyer.info,
    companyInfo: state.targetCompany.info.getIn(['info']),
    companyBuyer: state.targetCompany.target.getIn(['buyers', 0]),
    companyContacts: state.targetCompany.target.getIn(['contacts']),
    entityId: state.targetCompany.info.getIn(['info', 'id']),
    events: state.targetCompany.events,
    inputErrors: state.targetCompany.events.getIn(['current', 'inputErrors']),
    phoneCodes: state.targetCompany.target.get('phoneCodes'),
    user: state.auth.get('user', null),
    activities: state.eventActivities.getIn(['activities', 'all'], List()),
    backloggedActivities: state.eventActivities.get('backloggedActivityValues', List()),
  };
}

export { EventCompanyPopupContainer };

const shortcutWrap = publish(memo(EventCompanyPopupContainer));

export default connect(
  mapStateToProps,
  {
    reloadEvents,
    startEditCompanyEvent,
    changeCompanyEvent,
    checkNextActionExisting,
    saveCompanyEvent,
    findDirectors,
    findUsers,
    findAnalysts,
    findDealMakers,
    closeValidationError,
    resetCurrentEvent,
    loadCountryPhoneCodes,
    fetchEventActivities,
    sendOutgoingCall,
  },
  mergeProps,
  connectOptions,
)(InputErrorDisplay('closeValidationError', shortcutWrap));
